import { config } from './config';

export interface ApiErrorBody {
  error: { code: string; message: string; requestId?: string; details?: unknown };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ClientHooks {
  getToken: () => Promise<string | undefined>;
  /** The API no longer accepts this session (signed out everywhere, account gone). */
  onSessionEnded: (error: ApiError) => void;
}

/**
 * Talks to the DeskTouch API as the signed-in person. Wire types are declared
 * here rather than imported from the backend so the panel builds on its own.
 */
export function makeClient(hooks: ClientHooks) {
  const send = async (method: string, path: string, body?: unknown): Promise<Response> => {
    const token = await hooks.getToken();
    return fetch(`${config.apiBase}${path}`, {
      method,
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  };

  const fail = async (response: Response): Promise<never> => {
    const payload = (await response.json().catch(() => undefined)) as ApiErrorBody | undefined;
    const error = new ApiError(
      response.status,
      payload?.error?.code ?? 'unknown',
      payload?.error?.message ?? `Request failed (HTTP ${response.status})`,
      payload?.error?.details,
      payload?.error?.requestId,
    );
    if (
      response.status === 401 ||
      (response.status === 403 && (error.code === 'account_deleted' || error.code === 'account_disabled'))
    ) {
      hooks.onSessionEnded(error);
    }
    throw error;
  };

  const request = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
    const response = await send(method, path, body);
    if (!response.ok) return fail(response);
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  };

  return {
    me: () => request<Me>('GET', '/api/v1/me'),
    updateMe: (body: { displayName?: string; avatarUrl?: string; locale?: string }) =>
      request<Me>('PATCH', '/api/v1/me', body),
    usage: () => request<Usage>('GET', '/api/v1/me/usage'),
    identities: () => request<{ identities: Identity[] }>('GET', '/api/v1/me/identities'),
    unlinkIdentity: (id: string) => request<void>('DELETE', `/api/v1/me/identities/${id}`),

    plans: () => request<{ plans: Plan[] }>('GET', '/api/v1/plans'),
    billing: () => request<BillingOverview>('GET', '/api/v1/me/billing'),
    checkout: (body: CheckoutRequest) => request<CheckoutResult>('POST', '/api/v1/me/billing/checkout', body),
    payments: () => request<{ payments: Payment[] }>('GET', '/api/v1/me/billing/payments'),
    payment: (id: string) => request<Payment>('GET', `/api/v1/me/billing/payments/${id}`),
    redeemLicense: (code: string) =>
      request<{ plan: string; subscriptionId: string }>('POST', '/api/v1/licenses/redeem', { code }),

    devices: () => request<DeviceList>('GET', '/api/v1/me/devices'),
    renameDevice: (id: string, name: string) => request<Device>('PATCH', `/api/v1/devices/${id}`, { name }),
    removeDevice: (id: string) => request<void>('DELETE', `/api/v1/devices/${id}`),

    signOutEverywhere: async (): Promise<{ complete: boolean; revokedDevices: number }> => {
      const response = await send('POST', '/api/v1/me/sessions/revoke');
      const payload = (await response.json().catch(() => undefined)) as
        | { revokedDevices?: number; error?: { code?: string; details?: { revokedDevices?: number } } }
        | undefined;
      if (response.ok) return { complete: true, revokedDevices: payload?.revokedDevices ?? 0 };
      // 503 means our half succeeded and Logto's could not be done; that is a
      // result to report, not a failure to retry.
      if (response.status === 503 && payload?.error?.code === 'session_revocation_unavailable') {
        return { complete: false, revokedDevices: payload.error.details?.revokedDevices ?? 0 };
      }
      return fail(response);
    },
    exportData: async (format: 'json' | 'zip'): Promise<Blob> => {
      const response = await send('GET', `/api/v1/me/export${format === 'zip' ? '?format=zip' : ''}`);
      if (!response.ok) return fail(response);
      return response.blob();
    },
    deleteAccount: (reason?: string) =>
      request<{ id: string; deletedAt: string }>('DELETE', '/api/v1/me', reason ? { reason } : {}),
  };
}

export type ApiClient = ReturnType<typeof makeClient>;

export type FeatureValue = boolean | number | string;

export interface Me {
  id: string;
  email: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  locale: string | null;
  status: string;
  createdAt: string;
  lastLoginAt: string | null;
  subscription: { plan: string; status: string; currentPeriodEnd: string | null; cancelAtPeriodEnd: boolean };
  features: Record<string, FeatureValue>;
  plan: string;
}

export interface Usage {
  storageBytes: number;
  documentCount: number;
  devicesUsed: number;
  devicesMax: number | null;
}

export interface Identity {
  id: string;
  provider: string;
  email: string | null;
  isPrimary: boolean;
  linkedAt: string;
}

export interface PlanPriceSummary {
  key: string;
  currency: string;
  amountMinor: number;
  intervalMonths: number;
}

export interface Plan {
  key: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  features: Record<string, FeatureValue>;
  prices: PlanPriceSummary[];
}

export interface Price extends PlanPriceSummary {
  plan: string;
  planName: string;
}

export type PaymentStatus = 'pending' | 'completed' | 'cancelled' | 'failed' | 'refunded';

export interface Payment {
  id: string;
  status: PaymentStatus;
  plan: string;
  planName: string;
  description: string;
  amountMinor: number;
  currency: string;
  intervalMonths: number;
  provider: string;
  method: PaymentMethod | null;
  createdAt: string;
  completedAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  redirectUrl: string | null;
}

export interface SubscriptionSummary {
  plan: string;
  status: string;
  provider: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialEndsAt: string | null;
  gracePeriodEndsAt: string | null;
  organizationId: string | null;
  renewal: 'manual' | 'none';
}

export type PaymentMethod = 'blik' | 'card' | 'hosted';

export interface CheckoutRequest {
  priceKey: string;
  method: PaymentMethod;
  blikCode?: string;
  cardToken?: string;
  email?: string;
}

/** `redirect`: go to PayU's page or 3-D Secure. `wait`: the buyer confirms in the bank app. */
export type CheckoutResult =
  | { paymentId: string; action: 'redirect'; redirectUrl: string }
  | { paymentId: string; action: 'wait'; redirectUrl: null };

export interface BillingOverview {
  subscription: SubscriptionSummary;
  checkout: {
    available: boolean;
    provider: string | null;
    testMode: boolean;
    methods: PaymentMethod[];
    payu: { posId: string | null; sdkUrl: string } | null;
  };
  prices: Price[];
  payments: Payment[];
}

export interface Device {
  id: string;
  devicePublicId: string;
  name: string;
  platform: string;
  appVersion: string | null;
  connectedTo: string | null;
  connectedToName?: string | null;
  status: string;
  createdAt: string;
  lastSeenAt: string;
  revokedAt: string | null;
}

export interface DeviceList {
  devices: Device[];
  limit: number | null;
  used: number;
}
